import { HttpErrorResponse } from '@angular/common/http';
import { ApolloLink } from '@apollo/client/core';
import { RetryLink } from '@apollo/client/link/retry';
import { HttpLink } from 'apollo-angular/http';
import { TcsConfiguration } from 'src/app/app.config';
import { CompanyContextService } from 'src/app/shared/company-context.service';
import { Logger } from 'src/app/shared/tcs-logging/logger';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { onError } from '@apollo/client/link/error';
import { AppInsightsService } from 'src/lib/tcs-app-insights/app-insights-initializer.service';
import { NetworkError } from '@apollo/client/errors';
import { AuthenticationService } from 'src/lib/tcs-identity/authentication.service';
import { Router } from '@angular/router';
import { AppInsightsDependencyTrackingLink } from './app-insights-dependeny-tracking.apollo-link';
import { setAppInsightsOperationIdLink } from './app-insights-operation-id.apollo-link';
import { createAuthLink } from './auth.apollo-link';
import { createSecondarySourceLink } from './secondary-source-link';

export function createClientLinks(
  configuration: TcsConfiguration,
  companyContext: CompanyContextService,
  httpLink: HttpLink,
  logger: Logger,
  appInsights: AppInsightsService,
  authenticationService: AuthenticationService,
  router: Router,
) {
  const http = httpLink.create({
    uri: configuration.tcs.graphqlUri,
  });

  const retryLink = new RetryLink({
    delay: {
      initial: 500,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error, operation) => {
        /*
        We should only retry on non-user errors. We want user errors 
        to surface immediately (and not be delayed by the retry mechanism).
        */
        const canRetry = error instanceof HttpErrorResponse && error.status >= 500;
        const safeProps = createSafeProps({ error, operation });
        if (canRetry) {
          logger.warning('[Apollo.RetryLink] will retry request', safeProps);
        } else {
          logger.warning('[Apollo.RetryLink] will NOT retry request', safeProps);
        }
        return canRetry;
      },
    },
  });

  interface ErrorLookup {
    reg: RegExp;
    msg: string;
    type: 'error' | 'warning' | 'info';
  }
  const networkErrorMessages: ErrorLookup[] = [
    { reg: /0 Unknown Error/i, msg: '0 unknown error', type: 'warning' },
    { reg: /400 Bad Request/i, msg: '400 bad request', type: 'warning' },
    { reg: /401 Unauthorized/i, msg: '401 unauthorized', type: 'warning' },
    { reg: /500 Internal Server Error/i, msg: '500 internal server error', type: 'error' },
    { reg: /504 Gateway Timeout/i, msg: '504 gateway timeout', type: 'error' },
    // try get unknown error
    { reg: /(https?)?.*?: (.*)/i, msg: 'network error ($2)', type: 'error' },
    // final catch all
    { reg: /(.*)/, msg: 'network error ($1)', type: 'error' },
  ];
  const DEFAULT_NETWORK_ERROR_MESSAGE = networkErrorMessages[networkErrorMessages.length - 1];
  const lookupNetworkErrorMessage = (networkError: NetworkError): ErrorLookup => {
    const errorMessage = networkError?.message ?? '';
    const error =
      networkErrorMessages.find((err) => err.reg.test(errorMessage)) ??
      DEFAULT_NETWORK_ERROR_MESSAGE;
    return error.msg.indexOf('$') > -1
      ? { ...error, msg: errorMessage.replace(error.reg, error.msg) }
      : error;
  };

  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors != null) {
      const errors: any[] = [];
      let shouldRedirectToLogin = false;
      graphQLErrors.forEach(({ message, locations, path, extensions }) => {
        errors.push({ error: { message, locations, path, extensions } });
        if (message.toLowerCase().includes('requires authenticated user')) {
          shouldRedirectToLogin = true;
        }
      });
      if (shouldRedirectToLogin) {
        void authenticationService
          .logout({
            caller: 'apollo-client-links.createClientLinks',
            url: window.location.href,
          })
          .catch((error) => {
            logger.info(
              'apollo-client-links.createClientLinks: Failed to log out via AuthenticationService after authentication error',
              createSafeProps({ error }),
            );
            router.navigate(['/logout']);
          });
      }
      const opName = operation?.operationName;
      logger.error(
        `[GraphQL.onError] Operation: '${opName}'`,
        createSafeProps({ graphqlErrors: errors }),
      );
    } else if (networkError != null) {
      const message = lookupNetworkErrorMessage(networkError);
      const safeProps = createSafeProps({ networkError });
      const opName = operation?.operationName ?? 'unknown';
      logger[message.type](`[GraphQL.onError] ${message.msg}, Operation: ${opName}`, safeProps);
    } else {
      logger.error(`[GraphQL.onError] unknown error`);
    }
  });

  return ApolloLink.from([
    retryLink,
    errorLink,
    createAuthLink(companyContext),
    createSecondarySourceLink(),
    setAppInsightsOperationIdLink,
    new AppInsightsDependencyTrackingLink(configuration.tcs.graphqlUri, appInsights),
    http,
  ]);
}
